import { debounce } from "lodash";
import rg4js from "raygun4js";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import type { Option } from "react-bootstrap-typeahead/types/types";
import type {
  Case,
  ExternalCaseIEnumerableExternalPartnerResponse,
} from "@/briefpoint-client";
import {
  buildSearchString,
  CombinedCaseSearch,
  mergeCases,
} from "@/components/ConfirmInfo/ReviewPageOne";
import { CustomMenu } from "@/components/ConfirmInfo/utils";
import { Modal } from "@/components/Modals";
import { useCaseApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import type { ExternalCaseResponse } from "@/lib/types";
import { normalizeError } from "@/lib/utils";
import { initiateAuth } from "@/utils/ExternalPartner/utils";
import ExternalPartnerActions from "../ExternalPartnerActions/ExternalPartnerActions";
import styles from "./UploadProductionModal.module.scss";

interface UploadProductionModalProps {
  isShow: boolean;
  quickActionIsSaving: boolean;
  formError: string | undefined;
  isExternalConnectionActive?: boolean;
  cases: Case[] | undefined;
  quickActionCase: Case | undefined;
  setFormError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setQuickActionCase: React.Dispatch<React.SetStateAction<Case | undefined>>;
  handleQuickActionExternalUpload?: () => void;
  setShowUploadDocs: React.Dispatch<React.SetStateAction<boolean>>;
  handleChooseFile: (e: React.MouseEvent) => void;
  onClose: () => void;
  onConfirm: () => void;
}
export const UploadProductionModal = (props: UploadProductionModalProps) => {
  const { user } = useAuth();
  const [isLoadingCasesExternal, setIsLoadingCasesExternal] =
    useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const caseApi = useCaseApi();
  const [allExternalCases, setAllExternalCases] = useState<
    ExternalCaseResponse[] | undefined
  >();

  const errCheck = !props.quickActionCase;

  function onSubmit() {
    if (props.quickActionCase) {
      props.onConfirm();
    }
  }

  function handleCaseChange(e: Option[]): void {
    const val = e[0];
    setIsDirty(true);

    if (val) {
      const c = val as CombinedCaseSearch;
      let _case: Case | undefined = props.cases?.find(
        (x) =>
          x.id === c.internalId && x.integration?.identifier === c.externalId,
      );

      if (!_case) {
        const integration = c.externalId
          ? {
              partner: c.partner ?? undefined,
              identifier: c.externalId ?? undefined,
            }
          : undefined;
        _case = { shortTitle: c.title, integration };
      }

      props.setQuickActionCase(_case);
      setShowMenu(false);
      props.setFormError(undefined);
    }
  }

  function handleOnInputChange(text: string) {
    setIsDirty(true);
    if (text) {
      const isMatching = sortedCases.some((option) =>
        option.title?.toLowerCase().includes(text.toLowerCase()),
      );
      props.setQuickActionCase({ shortTitle: text });
      setShowMenu(isMatching);
    } else {
      props.setQuickActionCase(undefined);
      setShowMenu(false);
    }
    debouncedFilter(text);
  }

  const handleExternalCaseSet = (
    fetchedExternalCases:
      | ExternalCaseIEnumerableExternalPartnerResponse
      | undefined,
  ) => {
    if (fetchedExternalCases) {
      if (fetchedExternalCases.responseCode === 200) {
        setAllExternalCases(
          (fetchedExternalCases.data as ExternalCaseResponse[]) ?? [],
        );
      } else {
        if (fetchedExternalCases.responseCode === 401) {
          // initiate re-auth
          initiateAuth();
        } else {
          rg4js("send", {
            error: normalizeError(fetchedExternalCases),
          });
        }
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore handleExternalCaseSet
  const loadExternalCases = useCallback(
    async (filter?: string) => {
      if (user?.firmId && user?.externalConnection?.isActive) {
        setIsLoadingCasesExternal(true);

        const external = await caseApi.caseGetExternal({
          firmId: user.firmId,
          filter,
        });
        handleExternalCaseSet(external);
        setIsLoadingCasesExternal(false);
      }
    },
    [caseApi, user?.externalConnection?.isActive, user?.firmId],
  );

  const handleFilterChange = useCallback(
    async (filter?: string) => {
      await loadExternalCases(filter);
    },
    [loadExternalCases],
  );

  const debouncedFilter = useMemo(
    () =>
      debounce(async (filter?: string) => {
        await handleFilterChange(filter);
      }, 400),
    [handleFilterChange],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch user
  useEffect(() => {
    async function loadExternal() {
      await loadExternalCases();
    }
    loadExternal();
  }, [loadExternalCases, user]);

  const sortedCases: CombinedCaseSearch[] = mergeCases(
    props.cases,
    allExternalCases,
  ).sort((a, b) => {
    // Continue with the regular alphabetical sort by title
    const shortTitleA = a.title || "";
    const shortTitleB = b.title || "";
    return shortTitleA.localeCompare(shortTitleB);
  });

  function filterCallback(option: Option, props: { text: string }) {
    const caseOption = option as CombinedCaseSearch;
    return caseOption.title.toLowerCase().includes(props.text.toLowerCase());
  }

  return (
    <Modal
      show={props.isShow}
      onClose={props.onClose}
      onSecondaryAction={props.handleQuickActionExternalUpload}
      onConfirm={onSubmit}
      showConfirmButton={true}
      disableConfirmButton={errCheck || props.quickActionIsSaving}
      showCancelButton={false}
      cancelText="Cancel"
      confirmText={"Next"}
      size="lg"
      title={"Upload Production"}
    >
      <div className="mb-3">
        {props.formError && <Alert variant="destructive">{props.formError}</Alert>}
        <label
          data-testid="case-select-label"
          className="mt-1"
          htmlFor="case-select"
        >
          Case
        </label>
        <Typeahead
          className={`${styles.typeahead} ${
            props.quickActionCase && props.isExternalConnectionActive
              ? props.quickActionCase.integration?.partner
                ? styles[
                    `external-partner-${props.quickActionCase.integration.partner}`
                  ]
                : styles["external-partner-none"]
              : ""
          }`}
          onChange={(e) => handleCaseChange(e)}
          onInputChange={handleOnInputChange}
          selected={
            (props.quickActionCase &&
              [props.quickActionCase].map(
                (c) =>
                  new CombinedCaseSearch(
                    c.shortTitle ?? c.title ?? "",
                    buildSearchString(c),
                    c.integration?.partner,
                    c.integration?.identifier ?? undefined,
                    c.id,
                    false,
                  ),
              )) ||
            []
          }
          clearButton
          filterBy={filterCallback}
          flip={false}
          isInvalid={isDirty && !props.quickActionCase}
          id="case-select"
          emptyLabel="No matches found, this will create a new case."
          onFocus={() => setShowMenu(true)}
          onBlur={() => setShowMenu(false)}
          options={sortedCases}
          open={showMenu}
          size="sm"
          placeholder="Search for an existing case or create a new one..."
          labelKey="title"
          renderMenu={(results, menuProps, state) => (
            <CustomMenu
              results={results as CombinedCaseSearch[]}
              menuProps={menuProps}
              state={state}
              showSourceLogos={!!user?.externalConnection?.isActive}
            />
          )}
          isLoading={isLoadingCasesExternal}
        />
      </div>
      <ExternalPartnerActions
        toggleOnly={true}
        handleQuickActionExternalUpload={
          props.handleQuickActionExternalUpload ?? (() => {})
        }
        setShowUploadDocs={props.setShowUploadDocs}
        handleChooseFile={props.handleChooseFile}
      />
    </Modal>
  );
};
